import { GeneriekBerichtOnderwerp, useGetDossierBerichtenByOnderwerp } from "@/entities/bericht";
import { RoleGate, UserRole } from "@/features/auth";
import { DialogDescription, Empty, EmptyDescription, EmptyHeader } from "@/shared/components";
import { Dialog, DialogBody, DialogContainer, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog.tsx";
import { useArchiveVisibility } from "@/shared/hooks";
import { capitalize } from "@/shared/utils";
import { onderwerpLabelMap } from "../constants";
import { BerichtList } from "./bericht";
import { BerichtComposer } from "./bericht-composer";

type GeneriekeBerichtenDialogProps = {
    dossierId: string;
    onderwerp: GeneriekBerichtOnderwerp | GeneriekBerichtOnderwerp[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function GeneriekeBerichtenDialog({ dossierId, onderwerp, open, onOpenChange }: GeneriekeBerichtenDialogProps) {
    const { data: berichten, isPending, error } = useGetDossierBerichtenByOnderwerp(dossierId, onderwerp);
    const isArchived = useArchiveVisibility();

    if (error || isPending) return null;

    const onderwerpLabel = Array.isArray(onderwerp)
        ? onderwerp.map((onderwerp) => onderwerpLabelMap[onderwerp]).join(" en ")
        : onderwerpLabelMap[onderwerp];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer>
                <DialogHeader>
                    <DialogTitle>Berichten: {capitalize(onderwerpLabel)}</DialogTitle>
                    <DialogDescription className="sr-only">Schrijf een bericht naar Wissel</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <RoleGate roles={[UserRole.REGIEVOERDER]}>
                        {!isArchived && <BerichtComposer dossierId={dossierId} onderwerpInput={onderwerp} />}
                    </RoleGate>
                    {berichten.length > 0 ? (
                        <>
                            <BerichtList berichten={berichten} showOnderwerpBadge={Array.isArray(onderwerp) && onderwerp.length > 1} />
                        </>
                    ) : (
                        <GeenBerichten onderwerp={onderwerp} />
                    )}
                </DialogBody>
            </DialogContainer>
        </Dialog>
    );
}

type GeenBerichtenProps = {
    onderwerp: GeneriekBerichtOnderwerp | GeneriekBerichtOnderwerp[];
};

function GeenBerichten({ onderwerp }: GeenBerichtenProps) {
    const isArchived = useArchiveVisibility();

    const onderwerpLabel = Array.isArray(onderwerp)
        ? onderwerp.map((onderwerp) => onderwerpLabelMap[onderwerp]).join(" of ")
        : onderwerpLabelMap[onderwerp];

    const sentenceBegin = isArchived ? "Geen" : "Nog geen";

    return (
        <Empty className="items-start py-2">
            <EmptyHeader>
                <EmptyDescription>
                    {sentenceBegin}
                    {onderwerp === "Algemeen" ? " algemene berichten verstuurd." : ` berichten over ${onderwerpLabel} verstuurd.`}
                </EmptyDescription>
            </EmptyHeader>
        </Empty>
    );
}
